import { useState, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useEventEmitter } from '../../../hooks/useEventBus';
import { toast } from '../../../components/Toast';
import type { Demanda } from '../../../models';
import { 
  useAddObserversMutation, 
  useRemoveObserverMutation, 
  useRefuseObserverMutation 
} from './queries/useObserverMutations';

interface UseDemandObserversProps {
  demanda: Demanda;
  refetchHistorico: () => void;
  showAlert: (type: 'info' | 'warning' | 'error' | 'success', message: string, title?: string) => void;
  setAddObserversModal: React.Dispatch<React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading: boolean }>>;
}

export const useDemandObservers = ({
  demanda,
  refetchHistorico,
  showAlert,
  setAddObserversModal
}: UseDemandObserversProps) => {
  const { user: currentUser } = useAuth();
  const { emit } = useEventEmitter();
  
  // Mutations do TanStack Query
  const addObserversMutation = useAddObserversMutation(demanda.id || 0);
  const removeObserverMutation = useRemoveObserverMutation(demanda.id || 0);
  const refuseObserverMutation = useRefuseObserverMutation(demanda.id || 0, currentUser?.id);
  
  // Estados para adicionar observadores
  const [selectedObservers, setSelectedObservers] = useState<number[]>([]);
  const [observersToRemove, setObserversToRemove] = useState<number[]>([]);
  const [observersComment, setObserversComment] = useState('');
  const [isRefusingObserver, setIsRefusingObserver] = useState(false);

  // Verificar se o usuário atual é um observador ativo
  const isCurrentUserObserver = useMemo(() => {
    if (!currentUser?.id || !demanda.observadores) {
      return false;
    }

    return demanda.observadores.some((obs) => {
      // Verifica no formato Observador direto
      if ('observador_fk' in obs) {
        if (obs.observador_fk !== currentUser.id) return false;
        if (typeof obs.active === 'boolean' && !obs.active) return false;
        return true;
      }

      // Verifica no formato User com pivot
      if ('pivot' in obs && obs.pivot) {
        if (obs.pivot.observador_fk !== currentUser.id) return false;
        if (typeof obs.pivot.active === 'boolean' && !obs.pivot.active) return false;
        return true;
      }

      return false;
    });
  }, [demanda.observadores, currentUser?.id]);

  // Função para alternar remoção de observador
  const handleToggleObserverRemoval = (observadorFk: number) => {
    setObserversToRemove(prev => {
      if (prev.includes(observadorFk)) {
        return prev.filter(id => id !== observadorFk);
      } else {
        return [...prev, observadorFk];
      }
    });
  };

  // Função para gerenciar observadores (adicionar e remover em lote)
  const handleManageObservers = async () => {
    // Verificar se há alguma alteração para fazer
    if (selectedObservers.length === 0 && observersToRemove.length === 0) {
      showAlert('warning', 'Nenhuma alteração foi feita. Selecione observadores para adicionar ou marque observadores para remover.', 'Atenção');
      return;
    }

    if (!demanda.id) {
      showAlert('error', 'ID da demanda não encontrado.', 'Erro');
      return;
    }

    try {
      setAddObserversModal(prev => ({ ...prev, isLoading: true }));

      const operations: string[] = [];
      const results: string[] = [];

      // Adicionar novos observadores
      if (selectedObservers.length > 0) {
        operations.push('adição');
        try {
          const comment = observersComment.trim();
          await addObserversMutation.mutateAsync({
            observadores: selectedObservers,
            ...(comment && { comentario: comment })
          });
          results.push(`${selectedObservers.length} observador(es) adicionado(s)`);
          toast.success(`Observadores adicionados com sucesso.`);
        } catch (error) {
          console.error('Erro ao adicionar observadores:', error);
          throw error; // Propagar erro para o catch externo
        }
      }

      // Remover observadores selecionados
      if (observersToRemove.length > 0) {
        operations.push('remoção');
        for (const observadorFk of observersToRemove) {
          try {
            await removeObserverMutation.mutateAsync(observadorFk);
            toast.success(`Observador ${observadorFk} removido com sucesso.`);
          } catch (error) {
            console.error(`Erro ao remover observador ${observadorFk}:`, error);
          }
        }
        results.push(`${observersToRemove.length} observador(es) removido(s)`);
      }

      // Emitir evento de atualização da demanda
      emit('demand-updated', {
        demandId: demanda.id!,
        changes: {
          type: 'observers-managed',
          observersAdded: selectedObservers,
          observersRemoved: observersToRemove,
        }
      });

      // Fechar modal, limpar estados e mostrar toast
      handleCloseAddObserversModal();
      
      const operationsText = operations.join(' e ');
      const resultsText = results.join(', ');
      toast.success(`Operação de ${operationsText} de observadores realizada com sucesso! ${resultsText}.`);

      // Recarregar histórico
      refetchHistorico();

    } catch (error) {
      console.error('Erro ao gerenciar observadores:', error);
      setAddObserversModal(prev => ({ ...prev, isLoading: false }));
      showAlert('error', 'Erro ao gerenciar observadores. Tente novamente.', 'Erro');
    }
  };

  // Função para fechar modal de observadores e limpar estados
  const handleCloseAddObserversModal = () => {
    setSelectedObservers([]);
    setObserversToRemove([]);
    setObserversComment('');
    setAddObserversModal({ isOpen: false, isMinimized: false, isLoading: false });
  };

  // Função para recusar observação (o próprio usuário se remove)
  const handleRefuseObserver = async () => {
    if (!demanda.id) {
      showAlert('error', 'ID da demanda não encontrado.', 'Erro');
      return;
    }

    if (!isCurrentUserObserver) {
      showAlert('warning', 'Você não é um observador desta demanda.', 'Atenção');
      return;
    }

    setIsRefusingObserver(true);

    try {
      // Usar mutation para atualização otimista
      await refuseObserverMutation.mutateAsync();
      
      // Emitir evento de atualização
      emit('demand-updated', {
        demandId: demanda.id!,
        changes: {
          type: 'observer-refused',
          observerId: currentUser?.id,
        }
      });
      
      toast.success('Você não receberá mais atualizações desta demanda.');

      // Recarregar histórico para refletir a mudança
      refetchHistorico();
    } catch (error) {
      console.error('Erro ao recusar observação:', error);
      showAlert(
        'error',
        'Não foi possível recusar a observação. Tente novamente.',
        'Erro'
      );
    } finally {
      setIsRefusingObserver(false);
    }
  };

  return {
    // Estados
    selectedObservers,
    setSelectedObservers,
    observersToRemove,
    setObserversToRemove,
    observersComment,
    setObserversComment,
    isCurrentUserObserver,
    isRefusingObserver,
    
    // Funções
    handleToggleObserverRemoval,
    handleManageObservers,
    handleCloseAddObserversModal,
    handleRefuseObserver
  };
};